import { useRef, useState } from 'react';

import { Button } from 'cpool-components/input/Button';
import { ButtonVariant } from 'cpool-components/input/Button/types';
import type { APPLICANT_ATTRIBUTION } from 'cpool-constants';
import { AGGREGATE_NAMES, APPLICANT_ATTRIBUTIONS, LOAN_REQUEST_COMMANDS } from 'cpool-constants';
import { useTranslation } from 'react-i18next';
import { LuSend } from 'react-icons/lu';
import { toast } from 'react-toastify';

import { OpenClaimsGroup } from './OpenClaimsGroup';
import { APICommunicator } from '~/api/APICommunicator';
import { useAclPermissions } from '~/features/leadDetails/hooks/useAclPermissions';
import type { SchemaResolvedDemandedDocuments } from '~/features/leadDetails/schemas/demandedDocuments.schema';
import useCallbackWithErrorHandler from '~/hooks/useCallbackWithErrorHandler';

type Props = {
    hasSecondApplicant: boolean;
    leadId: string;
    openClaims: SchemaResolvedDemandedDocuments['documents'];
};

// Reminder carries the attribution so only that applicant is mailed
const OpenClaims: React.FC<Props> = ({ hasSecondApplicant, leadId, openClaims }) => {
    const { t } = useTranslation();
    const { permissions } = useAclPermissions({ loanRequestId: leadId });
    const isDisabled = !permissions.commands[AGGREGATE_NAMES.LOAN_REQUEST].remindDemandedDocuments;

    const claims = openClaims.map((claim) => ({
        attribution: claim.attribution,
        demandedDate: new Date(claim.demandedDate),
        name: claim.name,
    }));

    // Ref guards against double clicks before the disabled state has rendered
    const pendingRef = useRef(new Set<string>());
    const [pending, setPending] = useState<ReadonlySet<string>>(new Set());
    const pendingKey = (attribution?: APPLICANT_ATTRIBUTION) => attribution ?? 'single';

    const remind = useCallbackWithErrorHandler(
        async (attribution?: APPLICANT_ATTRIBUTION) => {
            const key = pendingKey(attribution);
            if (pendingRef.current.has(key)) return;
            pendingRef.current.add(key);
            setPending(new Set(pendingRef.current));
            try {
                await APICommunicator.sendCommand({
                    aggregateId: leadId,
                    aggregateName: AGGREGATE_NAMES.LOAN_REQUEST,
                    commandName: LOAN_REQUEST_COMMANDS.remindDemandedDocuments,
                    payload: { attribution },
                });
                toast.success(t('LEAD_DETAILS_GENERAL.EMAIL_SUCCESSFULLY'));
            } finally {
                pendingRef.current.delete(key);
                setPending(new Set(pendingRef.current));
            }
        },
        [leadId],
        t('LEAD_DETAILS_GENERAL.EMAIL_ERROR'),
    );

    const remindButton = (attribution?: APPLICANT_ATTRIBUTION) => (
        <Button
            variant={ButtonVariant.SECONDARY}
            iconLeft={<LuSend size={14} />}
            disabled={isDisabled || pending.has(pendingKey(attribution))}
            onClick={() => remind(attribution)}
        >
            {t('DOCUMENT_DEMANDER.OPEN_CLAIMS.SEND_MAIL')}
        </Button>
    );

    // A single applicant needs no grouping — one flat list with one reminder.
    if (!hasSecondApplicant)
        return <OpenClaimsGroup claims={claims} action={remindButton()} data-testid="open-claims-single" />;

    return (
        <div className="flex flex-col gap-4">
            {[APPLICANT_ATTRIBUTIONS.ANTRAGSTELLER_1, APPLICANT_ATTRIBUTIONS.ANTRAGSTELLER_2].map((attribution) => {
                const claimsForApplicant = claims.filter((claim) => claim.attribution === attribution);
                if (claimsForApplicant.length === 0) return null;

                return (
                    <OpenClaimsGroup
                        key={attribution}
                        claims={claimsForApplicant}
                        title={t(`APPLICANTS.${attribution}`)}
                        action={remindButton(attribution)}
                    />
                );
            })}
        </div>
    );
};

export default OpenClaims;
